import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiBase } from "../api";
import { useAuth } from "../components/AuthContext";

function ScoreRing({ score, maxScore = 100, size = 52 }) {
  const pct = Math.min(score / maxScore, 1);
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const color = pct >= 0.7 ? "#22c55e" : pct >= 0.4 ? "#f59e0b" : "#94a3b8";
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#1e293b" strokeWidth={5} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={5}
        strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
        strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2 + 1} textAnchor="middle" dominantBaseline="middle"
        fill={color} fontSize={size > 48 ? 14 : 11} fontWeight={900}>
        {Math.round(score)}
      </text>
    </svg>
  );
}

function MatchCard({ match, onBid }) {
  const { postedLoad: load, activeLoad, insight, score, scoreBreakdown, proximityMiles } = match;
  const rate = load.rateAmountCents ? `$${(load.rateAmountCents / 100).toLocaleString()}` : "Open";
  const rpm = load.rateAmountCents && load.distanceMiles
    ? `$${(load.rateAmountCents / 100 / load.distanceMiles).toFixed(2)}/mi` : "";

  return (
    <div style={{
      background: "#1e293b", borderRadius: 14, overflow: "hidden",
      border: score >= 70 ? "1px solid rgba(34,197,94,0.3)" : "1px solid rgba(255,255,255,0.06)",
      marginBottom: 10, boxShadow: score >= 70 ? "0 0 12px rgba(34,197,94,0.1)" : "0 1px 3px rgba(0,0,0,0.15)",
    }}>
      {/* AI Insight Banner */}
      <div style={{
        padding: "10px 14px",
        background: score >= 70 ? "rgba(34,197,94,0.08)" : "rgba(139,92,246,0.08)",
        borderBottom: "1px solid rgba(255,255,255,0.04)",
        fontSize: 13, color: "#e2e8f0", lineHeight: 1.4,
      }}>
        {insight}
      </div>

      <div style={{ padding: "12px 14px" }}>
        {/* Score + Load Info */}
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <ScoreRing score={score} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#f1f5f9" }}>
                  {load.referenceNumber}
                </div>
                {load.shipperName && (
                  <div style={{ fontSize: 11, color: "#64748b" }}>{load.shipperName}</div>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 18, fontWeight: 900, color: "#22c55e" }}>{rate}</div>
                {rpm && <div style={{ fontSize: 11, color: "#64748b" }}>{rpm}</div>}
              </div>
            </div>

            {/* Route */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12 }}>📍</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>
                {load.originCity}, {load.originState}
              </span>
              <span style={{ color: "#475569" }}>→</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>
                {load.destCity}, {load.destState}
              </span>
            </div>

            {/* Tags */}
            <div style={{ display: "flex", gap: 8, marginTop: 8, fontSize: 11, color: "#64748b", flexWrap: "wrap" }}>
              <span>📏 {proximityMiles} mi from drop</span>
              {load.distanceMiles && <span>🛣️ {load.distanceMiles} mi haul</span>}
              {load.pickupDate && (
                <span>📅 {new Date(load.pickupDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
              )}
            </div>

            {/* Score Breakdown */}
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              {[
                { label: "Proximity", val: scoreBreakdown.proximity, max: 40, icon: "📍" },
                { label: "Home", val: scoreBreakdown.headingHome, max: 30, icon: "🏠" },
                { label: "Timing", val: scoreBreakdown.timing, max: 20, icon: "⏰" },
                { label: "Rate", val: scoreBreakdown.rate, max: 10, icon: "💰" },
              ].map(s => (
                <span key={s.label} style={{
                  padding: "2px 6px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                  background: s.val / s.max >= 0.7 ? "rgba(34,197,94,0.12)" : "rgba(255,255,255,0.05)",
                  color: s.val / s.max >= 0.7 ? "#86efac" : "#94a3b8",
                }}>
                  {s.icon} {Math.round(s.val)}/{s.max}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Connected Load */}
        <div style={{
          marginTop: 10, padding: "8px 10px", borderRadius: 8,
          background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.04)",
          fontSize: 12, color: "#94a3b8",
        }}>
          🔗 Based on your active load <b style={{ color: "#e2e8f0" }}>{activeLoad.loadNumber}</b>
          {" "}delivering to <b style={{ color: "#e2e8f0" }}>{activeLoad.destCity}, {activeLoad.destState}</b>
          {activeLoad.deliveryDate && (
            <span> on {new Date(activeLoad.deliveryDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
          )}
        </div>
      </div>

      {/* Action */}
      <div style={{
        padding: "10px 14px", borderTop: "1px solid rgba(255,255,255,0.04)",
        display: "flex", gap: 8,
      }}>
        <button onClick={() => onBid(load.id)} style={{
          flex: 1, padding: "12px", borderRadius: 10, border: "none",
          background: "#22c55e", color: "white", fontWeight: 800, fontSize: 14,
          cursor: "pointer",
        }}>
          Place Bid
        </button>
        <button onClick={() => onBid(load.id, true)} style={{
          padding: "12px 16px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)",
          background: "transparent", color: "#94a3b8", fontWeight: 600, fontSize: 13,
          cursor: "pointer",
        }}>
          View
        </button>
      </div>
    </div>
  );
}

export default function CarrierBackhaulAlerts() {
  const navigate = useNavigate();
  const { carrierEmail } = useAuth();
  const apiBase = useMemo(() => getApiBase(), []);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/carrier/backhaul-matches`, {
          headers: { "x-carrier-email": carrierEmail || "tim@symplecode.com" },
        });
        const json = await res.json();
        setData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase, carrierEmail]);

  const handleBid = (loadId, viewOnly) => {
    navigate(`/app/carrier/loads/${loadId}`);
  };

  if (loading) return (
    <div style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>🤖</div>
      Analyzing your routes for backhaul opportunities...
    </div>
  );

  if (error) return (
    <div style={{ padding: 20, color: "#fca5a5" }}>Error: {error}</div>
  );

  const matches = data?.matches || [];
  const carrier = data?.carrier || {};
  const activeLoads = data?.activeLoads || [];

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}>
      {/* Header */}
      <div style={{
        padding: "16px 14px",
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{ fontSize: 20, fontWeight: 900, color: "#f1f5f9", marginBottom: 4 }}>
          🤖 AI Backhaul Matcher
        </div>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          {carrier.name && `${carrier.name} · `}
          {carrier.homeCity && `Home: ${carrier.homeCity}, ${carrier.homeState}`}
        </div>
      </div>

      {/* Active Loads Summary */}
      {activeLoads.length > 0 && (
        <div style={{ padding: "12px 14px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>
            Your Active Loads
          </div>
          {activeLoads.map((a, i) => (
            <div key={i} style={{
              padding: "8px 10px", borderRadius: 8, marginBottom: 4,
              background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.15)",
              fontSize: 13, color: "#e2e8f0", display: "flex", justifyContent: "space-between",
            }}>
              <span>
                <b>{a.loadNumber}</b> → {a.destCity}, {a.destState}
              </span>
              <span style={{ color: "#a78bfa", fontWeight: 600, fontSize: 11 }}>
                {a.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Matches */}
      <div style={{ padding: "0 14px" }}>
        <div style={{
          fontSize: 12, fontWeight: 700, color: "#94a3b8", marginBottom: 8, marginTop: 4,
          textTransform: "uppercase", letterSpacing: 1,
          display: "flex", justifyContent: "space-between",
        }}>
          <span>🔔 Backhaul Opportunities ({matches.length})</span>
        </div>

        {matches.length > 0 ? (
          matches.map((m, i) => (
            <MatchCard key={i} match={m} onBid={handleBid} />
          ))
        ) : (
          <div style={{
            padding: 30, textAlign: "center", borderRadius: 14,
            background: "#1e293b", border: "1px solid rgba(255,255,255,0.06)",
          }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🛣️</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#e2e8f0", marginBottom: 6 }}>
              No Backhaul Matches Yet
            </div>
            <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.5 }}>
              {data?.message || "We're watching for loads near your delivery points. You'll get an alert as soon as a matching load is posted."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
